import store, { AppDispatch } from 'core/store';
import { UpdateProductBody } from 'core/types';
import { updateProduct } from 'features/shared/inventory/store/InventoryActions';

export interface SaveProductUpdateResult {
  /** False only when the PATCH actually failed. */
  ok: boolean;
  /** False when `body` was empty and no request was issued. */
  changed: boolean;
}

/**
 * Single entry point for every homeowner product PATCH, wrapping `updateProduct`
 * with the two guards each call site needs but none of them can express alone.
 *
 * 1. **Success is not implied by the await resolving.** `createApiRequest`
 *    (core/util/api.tsx) catches API errors, dispatches `displayError` + the
 *    thunk's `onError`, and then falls through — it never rethrows, so the
 *    thunk resolves normally on a failed request. Call sites that awaited and
 *    then unconditionally dispatched `displaySuccess` therefore showed both an
 *    error toast and "Product Updated" for the same failure, and closed the
 *    editor, discarding the user's unsaved edits. Reading `inventory.error`
 *    immediately after the await is the available signal (`updateProductStart`
 *    resets it, so a non-null value belongs to this request); this mirrors the
 *    store.getState()-after-await pattern documented in CLAUDE.md and already
 *    used by resolveAnalyzedProductUpdate.
 * 2. **An empty body is not a no-op request.** Every caller builds `body`
 *    field-by-field from what actually changed, so pressing Save with nothing
 *    edited produced `PATCH {}`. Skipping the request keeps that free, and
 *    `changed: false` lets the caller close the editor without claiming it
 *    saved something.
 *
 * Callers should not dispatch their own error toast on `ok: false` —
 * `createApiRequest` has already surfaced the real API message.
 */
export const saveProductUpdate = async (
  dispatch: AppDispatch,
  homeId: number,
  productId: number,
  body: UpdateProductBody
): Promise<SaveProductUpdateResult> => {
  if (!Object.keys(body).length) {
    return { ok: true, changed: false };
  }
  await dispatch(updateProduct(homeId, productId, body));
  return { ok: store.getState().inventory.error === null, changed: true };
};
